import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { onAuthStateChanged, signInWithCustomToken, signOut } from "firebase/auth";
import { auth } from "../lib/firebase.js";
import { bootActiveStore } from "../lib/activeStore.js";

const AuthContext = createContext(null);

// Read the SSO bridge hash — `#portalToken=xxx&tenant=yyy&store=zzz` —
// minted by the portal's mintAppToken Cloud Function and dropped into the
// URL by /launch.html. Returns null if absent.
function readPortalHash() {
  const hash = window.location.hash || "";
  if (!hash.startsWith("#") || !hash.includes("portalToken=")) return null;
  const params = new URLSearchParams(hash.slice(1));
  const token = params.get("portalToken");
  if (!token) return null;
  return {
    token,
    tenantId: params.get("tenant") || null,
    storeId:  params.get("store")  || null,
  };
}

// Strip the hash — JWTs in URLs leak via history, referrers, analytics.
function clearHash() {
  if (window.history && window.history.replaceState) {
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
  } else {
    window.location.hash = "";
  }
}

// Remove the SEVRIC splash element from index.html once auth resolves.
function removeSplash() {
  try { document.getElementById("sevric-splash")?.remove(); } catch {}
}

export function AuthProvider({ children }) {
  const [state, setState] = useState({
    status: "loading",
    user:   null,
    profile: null,
    error: null,
  });

  // Race-prevention: when we're mid-consume of a portal token, don't let the
  // initial onAuthStateChanged(null) callback bounce the user to /signin.
  // Firebase's auth state listener fires synchronously on mount with the
  // last-known user (which is null on a fresh tab) BEFORE signInWithCustomToken
  // has a chance to complete — without this guard, the user is bounced
  // mid-bridge. consumingRef.current stays true until the token call resolves
  // (success OR failure), at which point onAuthStateChanged fires again with
  // the real user (or stays null on failure → "error" status).
  const consumingRef = useRef(false);

  // 1. Consume the portal SSO token if present. signInWithCustomToken triggers
  //    onAuthStateChanged, which the listener below picks up.
  useEffect(() => {
    const bridge = readPortalHash();
    if (!bridge) return;
    clearHash();
    consumingRef.current = true;
    signInWithCustomToken(auth, bridge.token)
      .catch((err) => {
        console.error("[battery] portal SSO failed:", err);
        setState((s) => ({ ...s, status: "error", error: err.message || String(err) }));
        removeSplash();
      })
      .finally(() => {
        // Whether success or failure, we're no longer mid-consume. Subsequent
        // onAuthStateChanged calls reflect the actual auth state.
        consumingRef.current = false;
      });
  }, []);

  // 2. Subscribe to auth state. Read claims directly — no Firestore round-trip
  //    on every nav. Custom claims are the authoritative source.
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        // CRITICAL: don't transition to "unauthenticated" while we're still
        // in the middle of consuming a portal token. The listener fires once
        // immediately on mount with null (browser cold-start has no user yet),
        // and signInWithCustomToken hasn't resolved. Without this guard,
        // RequireAuth would bounce the user to /signin before the token
        // could land. We stay in "loading" until consumingRef clears.
        if (consumingRef.current) return;
        setState({ status: "unauthenticated", user: null, profile: null, error: null });
        removeSplash();
        return;
      }
      let claims = {};
      try {
        const tr = await user.getIdTokenResult();
        claims = tr.claims || {};
      } catch { /* fall through, profile defaults to whatever Firebase gave us */ }

      const profile = {
        uid:           user.uid,
        email:         user.email,
        // displayName is the user's real name (e.g., "Andrew Monahan"). It
        // is null until they set it on first login. Distinct from `name`,
        // which falls back to the email local-part so the UI never renders
        // blank — but anywhere we record an author snapshot we use this so
        // historical entries don't get email-derived names.
        displayName:   user.displayName || null,
        name:          user.displayName || (user.email || "").split("@")[0],
        // Per-app role on this app:
        role:          claims.apps?.battery || "viewer",
        tenantId:      claims.tenantId  || null,
        storeId:       claims.storeId   || null,
        storeAccess:   claims.storeAccess || (claims.storeId ? [claims.storeId] : []),
        portalManaged: claims.issuedVia === "portal",
        isPlatformAdmin: !!claims.isPlatformAdmin,
        isGroupAdmin:    !!claims.isGroupAdmin,
        isStoreAdmin:    !!claims.isStoreAdmin,
      };
      // Seed the active-store singleton from the user's claims. If they
      // have only one store in storeAccess this is a no-op; if they have
      // multiple, the switcher in the topbar picks up the persisted choice.
      bootActiveStore({
        uid: profile.uid,
        storeId: profile.storeId,
        storeAccess: profile.storeAccess,
      });
      setState({ status: "authenticated", user, profile, error: null });
      removeSplash();
    });
    return unsub;
  }, []);

  // Refresh the profile from the current Firebase user — used after a
  // first-login displayName save, since `updateProfile` doesn't trigger
  // onAuthStateChanged on its own. Mirrors the listener's build logic so
  // the resulting profile shape is identical.
  async function refreshProfile() {
    const user = auth.currentUser;
    if (!user) return;
    try { await user.reload(); } catch { /* non-fatal */ }
    let claims = {};
    try {
      const tr = await user.getIdTokenResult();
      claims = tr.claims || {};
    } catch { /* ignore */ }
    const profile = {
      uid:           user.uid,
      email:         user.email,
      displayName:   user.displayName || null,
      name:          user.displayName || (user.email || "").split("@")[0],
      role:          claims.apps?.battery || "viewer",
      tenantId:      claims.tenantId  || null,
      storeId:       claims.storeId   || null,
      storeAccess:   claims.storeAccess || (claims.storeId ? [claims.storeId] : []),
      portalManaged: claims.issuedVia === "portal",
      isPlatformAdmin: !!claims.isPlatformAdmin,
      isGroupAdmin:    !!claims.isGroupAdmin,
      isStoreAdmin:    !!claims.isStoreAdmin,
    };
    setState((s) => ({ ...s, user, profile }));
  }

  const value = useMemo(
    () => ({ ...state, signOut: () => signOut(auth), refreshProfile }),
    [state],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
